import { useSetAtom, useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import {
  closeSecondaryToolbarMenuSetter,
  Service,
  takeSnapshotSetter,
  isAgentsEnabledAtom,
  AssetType,
  getAssetTypeFromAppId,
} from "@zapier/canvas-state";
import { App, AppPicker } from "@zapier/app-picker";

import useUpdateNode from "../../../hooks/useUpdateNode";
import useServiceById from "../../../hooks/useServiceById";
import { LinkRemovalWarning } from "../../LinkRemovalWarning";
import { NodeToolbarTop } from "../NodeToolbarTop";

type Props = {
  selectedApps?: Service[];
  isZapNode: boolean;
  hasLink: boolean;
};

/**
 * Determines the asset type for a given app.
 */
function getAssetTypeFromApp(app: App | null): AssetType {
  if (!app) return undefined;

  const appId = app?.currentImplementation?.id ?? "";

  return app.name === "Zap" ? "zap" : getAssetTypeFromAppId(appId);
}

export function FloatingAppTypeahead({
  selectedApps,
  isZapNode,
  hasLink,
}: Props) {
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();

  const getService = useServiceById();
  const appId = selectedApps?.[0]?.currentImplementation?.id ?? "";
  const service = getService(appId);

  const hasApp = selectedApps && selectedApps.length > 0;
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [newSelectedApp, setNewSelectedApp] = useState<App | null>(null);

  const isAgentsEnabled = useAtomValue(isAgentsEnabledAtom);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    setNewSelectedApp(null);
  }, []);

  const handleAppChange = useCallback(
    (app: App) => {
      takeSnapshot();

      const newAssetType = getAssetTypeFromApp(app);

      if (newAssetType === "zap") {
        updateNodeAndSave({
          type: "zap",
          data: {
            color: undefined, // Clear color in order to fall back to default zap node colors
          },
        });
      } else {
        updateNodeAndSave({
          type: "default",
          data: {
            app: app.name,
            services: [
              {
                currentImplementation: {
                  id: app.currentImplementation.id,
                },
              },
            ],
            ...(newAssetType ? { color: undefined } : {}), // Clear color in order to fall back to default asset node colors
          },
        });
      }

      dismissWarning();
      closeSecondaryMenu();
    },
    [dismissWarning, takeSnapshot, updateNodeAndSave, closeSecondaryMenu],
  );

  const onSelectionChange = useCallback(
    (app: App) => {
      const newAssetType = getAssetTypeFromApp(app);
      const hasAssetType = Boolean(newAssetType);

      // Show warning only when swapping from a third-party app to a first-party app
      if (hasLink && hasAssetType) {
        setNewSelectedApp(app);
        setShowWarning(true);
      } else {
        handleAppChange(app);
      }
    },
    [handleAppChange, hasLink],
  );

  const handleRemoveApp = useCallback(() => {
    takeSnapshot();

    if (isZapNode) {
      updateNodeAndSave({
        type: "default",
      });
    } else {
      updateNodeAndSave({
        data: {
          app: undefined,
          services: undefined,
          tableId: undefined,
          projectId: undefined,
          pageId: undefined,
          chatbotId: undefined,
          agentId: undefined,
        },
      });
    }
  }, [isZapNode, takeSnapshot, updateNodeAndSave]);

  const newAssetType = getAssetTypeFromApp(newSelectedApp);

  return (
    <NodeToolbarTop>
      {showWarning && newAssetType && (
        <LinkRemovalWarning
          assetType={newAssetType}
          onConfirm={() => handleAppChange(newSelectedApp!)}
          onDismiss={dismissWarning}
        />
      )}
      <AppPicker
        onSelect={onSelectionChange}
        onClose={closeSecondaryMenu}
        selectedApp={isZapNode ? "Zap" : (service?.name ?? "")}
        onRemove={isZapNode || hasApp ? handleRemoveApp : undefined}
        isDarkMode={true}
        shouldCloseOnClickOutside={false}
        showAgentsNodes={isAgentsEnabled}
      />
    </NodeToolbarTop>
  );
}
